import { getCalendarEventFeedbackWindowState } from 'shared-helpers/calendar';
import { useDateNow, useReduxState } from 'hooks';
import api from 'api';
import { ConfirmButton } from 'components/common';

interface Props {
  disabled?: boolean;
  submissionId?: number;
  updateResponses: () => Promise<void>;
}

/**
 * Deletes feedback responses for the event: a single one when a submissionId
 * is given, every response on the event when it is left out. Owners and
 * superadmins only - the API refuses the request for anyone else.
 */
const CalendarEventFeedbackResponseDelete = ({
  disabled,
  submissionId,
  updateResponses
}: Props) => {
  const { dateNow: now } = useDateNow();

  const {
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  // a closed form cannot be reopened by deleting responses, so the user is
  // warned that the deletion is final
  const isFormClosed =
    !calendarEvent.feedbackEnabled ||
    getCalendarEventFeedbackWindowState({ event: calendarEvent, now }) ===
      'closed';

  const handleConfirm = async () => {
    await api.delete(
      `/admin/calendar-events/${calendarEvent.id}/feedback-submission${
        submissionId ? `/${submissionId}` : ''
      }`
    );
    await updateResponses();
    return true;
  };

  const buttonProps = submissionId
    ? {
        buttonText: messages.commonDelete,
        confirmText:
          `${messages.eventCalendarFeedbackResponseDeleteConfirm}. ` +
          (isFormClosed
            ? messages.eventCalendarFeedbackResponseDeleteConfirmClosed
            : messages.eventCalendarFeedbackResponseDeleteConfirmOpen),
        confirmTitle: messages.commonDelete,
        successMessage: messages.eventCalendarFeedbackResponseDeleteSuccess
      }
    : {
        buttonText: messages.commonDeleteAll,
        confirmText:
          `${messages.eventCalendarFeedbackResponsesDeleteAllConfirm}. ` +
          (isFormClosed
            ? messages.eventCalendarFeedbackResponseDeleteConfirmClosed
            : messages.eventCalendarFeedbackResponseDeleteConfirmOpen),
        confirmTitle: messages.commonDeleteAll,
        successMessage: messages.eventCalendarFeedbackResponsesDeleteAllSuccess
      };

  return (
    <ConfirmButton
      actionText={messages.commonDelete}
      buttonIcon="trash-alt"
      buttonIsDropdown={true}
      category="danger"
      disabled={disabled}
      onConfirm={handleConfirm}
      {...buttonProps}
    />
  );
};

export default CalendarEventFeedbackResponseDelete;
